import { useState } from 'react';
import { toast } from 'react-toastify';
import { MIN_CAMPAIGN_AMOUNT, MAX_CAMPAIGN_AMOUNT } from '@/features/campaigns/constants/campaignOptions';
import { useAiGeneratePreview } from '@/features/campaigns/api/useCampaignMutations';
import { useAppSelector } from '@/shared/store/hooks';
import { requiresAgencyClient } from '@/shared/config/roles';
import {
  todayIsoDate,
  tomorrowIsoDate,
  campaignDateMax,
  validateCampaignDateRange,
} from '@/features/campaigns/utils/campaignUtils';
import { useInstantAdAccess } from '@/features/profile/hooks/useInstantAdAccess';
import { getCurrencyCode } from '@/shared/lib/format/money';
import type { AIBuilderProps } from './AIBuilder.types';

/** Owns the AI brief form's validation, field-clearing helpers, and plan-generation mutation. */
export function useAiBuilderForm({
  formData,
  onPreviewGenerated,
}: AIBuilderProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [advanced, setAdvanced] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const generate = useAiGeneratePreview();
  const needsClient = requiresAgencyClient(
    useAppSelector((s) => s.auth.user?.role),
  );
  const instantAdAccess = useInstantAdAccess();
  const minStart = instantAdAccess ? todayIsoDate() : tomorrowIsoDate();
  const maxStart = campaignDateMax(minStart);
  const currency = getCurrencyCode() === 'INR' ? 'INR' : getCurrencyCode();

  const clearFieldError = (field: string) => {
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (needsClient && !formData.agencyClientId) {
      next.agencyClientId = 'Please select a client';
    }
    const budget = Number(formData.budget);
    if (!formData.budget || Number.isNaN(budget)) {
      next.budget = 'Enter a budget';
    } else if (budget < MIN_CAMPAIGN_AMOUNT) {
      next.budget = `Minimum is ${currency}${MIN_CAMPAIGN_AMOUNT}`;
    } else if (budget > MAX_CAMPAIGN_AMOUNT) {
      next.budget = `Maximum is ${currency}${MAX_CAMPAIGN_AMOUNT.toLocaleString('en-IN')}`;
    }
    Object.assign(
      next,
      validateCampaignDateRange(formData.startDate, formData.endDate, minStart, {
        requiredStart: 'Pick a start date',
        requiredEnd: 'Pick an end date',
      }),
    );
    if (!formData.targetCities.length) next.targetCities = 'Select at least one city';
    if (!formData.audienceCategory) {
      next.audienceCategory = 'Choose a category';
    }
    if (!formData.adDuration) next.adDuration = 'Choose duration';
    if (!formData.vision.trim() || formData.vision.trim().length < 10) {
      next.vision = 'Add a bit more detail (at least 10 characters)';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      toast.error(Object.values(next)[0]);
    }
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    let latitude: number | undefined = undefined;
    let longitude: number | undefined = undefined;

    if (navigator.geolocation) {
      setIsLocating(true);
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
          });
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch {

      } finally {
        setIsLocating(false);
      }
    }

    const payload = {
      budget: Number(formData.budget),
      startDate: formData.startDate,
      endDate: formData.endDate,
      targetCities: formData.targetCities,
      audienceCategory: formData.audienceCategory,
      adDuration: formData.adDuration,
      vision: formData.vision.trim(),
      economicActivity: formData.economicActivity || undefined,
      lifestyle: formData.lifestyle || undefined,
      industry: formData.industry || undefined,
      subIndustry: formData.subIndustry || undefined,
      productName: formData.productName || undefined,
      company: formData.company || undefined,
      brand: formData.brand || undefined,
      locationType: formData.locationType || undefined,
      screenType: formData.screenType || undefined,
      VenueType: formData.VenueType || undefined,
      VenueSubType: formData.VenueSubType || undefined,
      location: formData.targetCities.join(', '),
      category: formData.audienceCategory,
      outcomes: formData.vision.trim(),
      customDuration: Number(formData.adDuration),
      ...(formData.agencyClientId ? { agencyClientId: formData.agencyClientId } : {}),
      ...(latitude !== undefined ? { latitude } : {}),
      ...(longitude !== undefined ? { longitude } : {}),
    };

    generate.mutate(payload, {
      onSuccess: (res) => {
        if (!res?.data) {
          toast.error(res?.message || 'No AI plan returned');
          return;
        }
        onPreviewGenerated({ ...res.data, payload });
      },
    });
  };

  return {
    errors,
    clearFieldError,
    advanced,
    setAdvanced,
    minStart,
    maxStart,
    currency,
    isGenerating: generate.isPending || isLocating,
    handleSubmit,
  };
}
